// S26: Admin User Detail Page
import { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Button,
  Tabs,
  Switch,
  Input,
  Modal,
  message,
  Spin,
  Alert,
  Typography,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  PercentageOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PageHeader } from '../components/PageHeader';

dayjs.extend(relativeTime);
import {
  adminUsersEndpoints,
  type UserDetail,
  type ClosureRequest,
} from '../api/adminUsersEndpoints';

const { Title, Text } = Typography;

export function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [user, setUser] = useState<UserDetail | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editedName, setEditedName] = useState('');

  useEffect(() => {
    if (id) {
      fetchUserDetail();
    }
  }, [id]);

  const fetchUserDetail = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await adminUsersEndpoints.getUser(id);
      setUser(data.user);
      setEditedName(data.user.full_name);
    } catch (error: any) {
      console.error('Failed to fetch user detail:', error);
      message.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (active: boolean) => {
    if (!id || !user) return;

    setUpdating(true);
    try {
      const data = await adminUsersEndpoints.updateUser(id, { active });
      setUser(data.user);
      message.success(`User ${active ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      message.error('Failed to update user status');
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateName = async () => {
    if (!id || !editedName.trim()) return;

    setUpdating(true);
    try {
      const data = await adminUsersEndpoints.updateUser(id, {
        full_name: editedName.trim(),
      });
      setUser(data.user);
      setEditModalOpen(false);
      message.success('Name updated successfully');
    } catch (error: any) {
      console.error('Failed to update name:', error);
      message.error('Failed to update name');
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <Alert
        title="User Not Found"
        description="The requested user could not be found."
        type="error"
        showIcon
      />
    );
  }

  const tabItems = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <Space orientation="vertical" size="large" style={{ width: '100%' }}>
          {/* S41: Show active closure request if exists */}
          {user.active_closure_request && (
            <Alert
              type={
                user.active_closure_request.status === 'pending' ? 'warning' :
                user.active_closure_request.status === 'cancelled' ? 'info' :
                user.active_closure_request.status === 'completed' ? 'success' : 'warning'
              }
              showIcon
              message={
                <Space>
                  <strong>
                    {user.active_closure_request.status === 'pending' && 'Account Closure Pending'}
                    {user.active_closure_request.status === 'processing' && 'Processing Closure'}
                    {user.active_closure_request.status === 'completed' && 'Account Closed'}
                    {user.active_closure_request.status === 'cancelled' && 'Closure Request Cancelled'}
                  </strong>
                </Space>
              }
              description={
                <Space orientation="vertical" size="small">
                  <Text>
                    Initiated by: <Tag>{user.active_closure_request.initiated_by}</Tag>
                  </Text>
                  <Text>
                    Requested: {dayjs(user.active_closure_request.requested_at).format('MMM D, YYYY h:mm A')}
                  </Text>
                  {user.active_closure_request.cancelled_at && (
                    <Text>
                      Cancelled: {dayjs(user.active_closure_request.cancelled_at).format('MMM D, YYYY h:mm A')}
                    </Text>
                  )}
                  {user.active_closure_request.processed_at && (
                    <Text>
                      Processed: {dayjs(user.active_closure_request.processed_at).format('MMM D, YYYY h:mm A')}
                    </Text>
                  )}
                  {user.active_closure_request.exit_effective_at && (
                    <Text>
                      Exit Date: {dayjs(user.active_closure_request.exit_effective_at).format('MMM D, YYYY')}
                    </Text>
                  )}
                  {user.active_closure_request.reason && (
                    <Text>
                      Reason: {user.active_closure_request.reason}
                    </Text>
                  )}
                  {user.active_closure_request.status === 'pending' && (
                    <Text type="secondary">
                      This account will be closed during the next weekly settlement.
                    </Text>
                  )}
                </Space>
              }
            />
          )}

          <Alert
            title="Financial Snapshot"
            description="Current financial status and eligibility information"
            type="info"
            showIcon
          />

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Base Principal"
                  value={user.base_principal_cents}
                  precision={2}
                  prefix={<DollarOutlined />}
                  formatter={(value) => formatCurrency(Number(value))}
                />
                {user.base_principal_initialized_at && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Since {dayjs(user.base_principal_initialized_at).format('MMM D, YYYY')}
                  </Text>
                )}
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Withdrawable"
                  value={user.withdrawable_cents}
                  precision={2}
                  prefix={<DollarOutlined />}
                  formatter={(value) => formatCurrency(Number(value))}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Ownership"
                  value={user.ownership_percent}
                  precision={4}
                  suffix="%"
                  prefix={<PercentageOutlined />}
                />
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Profit This Week"
                  value={user.profit_week_cents}
                  precision={2}
                  prefix={<DollarOutlined />}
                  formatter={(value) => formatCurrency(Number(value))}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Contribution Details" size="small">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Min Required Contribution">
                {formatCurrency(user.min_required_contribution_cents)}
              </Descriptions.Item>
              <Descriptions.Item label="Contributed Amount">
                {formatCurrency(user.contributed_amount_cents)}
              </Descriptions.Item>
              <Descriptions.Item label="Contributed At">
                {user.contributed_at ? dayjs(user.contributed_at).format('MMM D, YYYY h:mm A') : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Reinvest Percent">
                {user.reinvest_percent}%
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Eligibility Status" size="small">
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Eligible for Earnings">
                <Tag color={user.eligible_for_earnings ? 'green' : 'orange'} icon={user.eligible_for_earnings ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                  {user.eligible_for_earnings ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {user.eligibility_status}
              </Descriptions.Item>
              {user.contribution_shortfall_cents > 0 && (
                <Descriptions.Item label="Shortfall">
                  {formatCurrency(user.contribution_shortfall_cents)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="Activity Summary" size="small">
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Contributions">
                {user.contributions_count}
              </Descriptions.Item>
              <Descriptions.Item label="Profit Allocations">
                {user.profit_allocations_count}
              </Descriptions.Item>
              <Descriptions.Item label="Withdrawal Requests">
                {user.withdrawal_requests_count}
              </Descriptions.Item>
              <Descriptions.Item label="Weekly Settlements">
                {user.weekly_settlements_count}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Space>
      ),
    },
    {
      key: 'history',
      label: 'History',
      children: (
        <Alert
          title="History Coming Soon"
          description="Contributions, allocations, withdrawals, and settlements history will be displayed here. This connects to existing S19 (allocations), S22 (withdrawals), and S21 (settlements) data."
          type="info"
          showIcon
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="User Details"
        description={`Viewing details for ${user.full_name}`}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/users')}
          >
            Back to Users
          </Button>
        }
      />

      {/* Header Card */}
      <Card style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Title level={3} style={{ margin: 0 }}>
                  {user.full_name}
                </Title>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => setEditModalOpen(true)}
                >
                  Edit Name
                </Button>
              </Space>
            </Col>
            <Col>
              <Space>
                <Text>Active Status:</Text>
                <Switch
                  checked={user.active}
                  onChange={handleToggleActive}
                  loading={updating}
                  checkedChildren={<CheckCircleOutlined />}
                  unCheckedChildren={<CloseCircleOutlined />}
                />
              </Space>
            </Col>
          </Row>

          <Descriptions bordered column={2}>
            <Descriptions.Item label="Account ID">{user.account_id}</Descriptions.Item>
            <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
            <Descriptions.Item label="Role">
              <Tag icon={user.role === 'admin' ? <TeamOutlined /> : <UserOutlined />} color={user.role === 'admin' ? 'purple' : 'blue'}>
                {user.role.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              <Tag color={user.country === 'USA' ? 'blue' : 'green'}>
                {user.country}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={user.active ? 'success' : 'default'} icon={user.active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                {user.active ? 'Active' : 'Inactive'}
              </Tag>
            </Descriptions.Item>
            {user.invited_by && (
              <>
                <Descriptions.Item label="Invited By">
                  {user.invited_by.full_name} ({user.invited_by.email})
                </Descriptions.Item>
                <Descriptions.Item label="Invited At">
                  <Space>
                    <CalendarOutlined />
                    {user.invited_at ? dayjs(user.invited_at).format('MMM D, YYYY h:mm A') : 'N/A'}
                  </Space>
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Member Since">
              <Space>
                <CalendarOutlined />
                {dayjs(user.created_at).format('MMM D, YYYY')}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated">
              {dayjs(user.updated_at).fromNow()}
            </Descriptions.Item>
          </Descriptions>
        </Space>
      </Card>

      {/* Tabs for different sections */}
      <Card>
        <Tabs items={tabItems} defaultActiveKey="overview" />
      </Card>

      {/* Edit Name Modal */}
      <Modal
        title="Edit User Name"
        open={editModalOpen}
        onOk={handleUpdateName}
        onCancel={() => setEditModalOpen(false)}
        confirmLoading={updating}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Text>Update the display name for this user:</Text>
          <Input
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            placeholder="Full Name"
            maxLength={100}
          />
          <Alert
            title="Note"
            description="Only the display name can be edited. Email, country, and financial fields are immutable."
            type="info"
            showIcon
            style={{ marginTop: 8 }}
          />
        </Space>
      </Modal>
    </div>
  );
}
